import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../context/useUser";
import Header from "./header";
import Footer from "./footer";
import Sidebar from "./sidebar";
import { fetchRequiredCategories } from "../utils/checkMissingSelections";
import { apiFetch } from "../api/client";
import { getImageUrl } from "../utils/imageUrl";
import infored from '../assets/info-red.png'
import infoyellow from '../assets/info-yellow.png';

const url = process.env.REACT_APP_BACKEND_CONNECTION;

export default function OptionPage({
  title,
  parentCategory,
  previousParentCategory,
  currentStep,
  nextRoute,
  imageMap = {},
  checkMissingSelections = null,
  customLoader = null,
  customRenderer = null,
  hideTitle = false,
  pageType = null
}) {

  const { user, selectedCompany } = useUser();
  const navigate = useNavigate();
  const [options, setOptions] = useState({});
  const [selectedOptions, setSelectedOptions] = useState({ byCategory: {}, all: [] });
  const [dependencies, setDependencies] = useState({});
  const [missingSelections, setMissingSelections] = useState([]);
  const [saving, setSaving] = useState(false);
  const [summaryId, setSummaryId] = useState(null);
  const [infoPopup, setInfoPopup] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [selections, setSelections] = useState([]);
  const [voteStatus, setVoteStatus] = useState(null);
  const [votingBusy, setVotingBusy] = useState(false);
  const [voteOutcomeChanges, setVoteOutcomeChanges] = useState({});

  const hasEditRights = [
    "chairman",
    "selection_delegate"
  ].includes(userRole);

  const isBoardMember = userRole === "board_member";
  const votingOpen = voteStatus?.status === "vote_launched";
  const canBrowseReadOnlyPages = isBoardMember
    && ["vote_launched", "launch_count", "complete"].includes(voteStatus?.status);
  const canEditSelections = hasEditRights && voteStatus?.status === "incomplete";
  const supportsVoting = pageType !== "furniture-arrangement";

  useEffect(() => {
    if (!user?.token) return;
    if (selectedCompany?.summaryId && selectedCompany?.staircase_type_id) return;

    navigate("/taloyhtio-ja-rapputyyppi");
  }, [navigate, selectedCompany?.staircase_type_id, selectedCompany?.summaryId, user?.token]);

  const buildSelectedMap = (selectedRows) => {
    const selectedMap = { byCategory: {}, all: [] };

    selectedRows.forEach(sel => {
      const key = sel.category_name;
      if (!key) return;

      if (sel.item_type === "staircase_type") {
        selectedMap.byCategory[key] = sel.staircase_type_id;
        selectedMap.all.push(sel.staircase_type_id);
      } else if (sel.item_type === "floor_plan") {
        selectedMap.byCategory[key] = sel.floor_plan_id;
        selectedMap.all.push(sel.floor_plan_id);
      } else if (sel.item_type === "option") {
        selectedMap.byCategory[key] = sel.option_id;
        selectedMap.all.push(sel.option_id);
      } else if (sel.item_type === "product") {
        selectedMap.byCategory[key] = sel.product_id;
        selectedMap.all.push(sel.product_id);
      }
    });

    return selectedMap;
  };

  const fetchVoteResultsIfFinal = async (summaryId, status) => {
    if (!supportsVoting || !["launch_count", "complete"].includes(status)) {
      return [];
    }

    try {
      const voteResultsData = await apiFetch(`/form/summary/${summaryId}/vote/results`, {
        token: user.token,
      });
      return voteResultsData?.results || [];
    } catch (err) {
      console.error("Failed to load vote results for final selection mapping:", err);
      return [];
    }
  };

  const applyVoteResultOverrides = (baseSelectedMap, optionsMap, voteResults) => {
    const byCategory = { ...(baseSelectedMap?.byCategory || {}) };
    const changesByCategory = {};

    voteResults.forEach((result) => {
      const category = result?.category_name;
      const winningSelectableId = Number(result?.winning_selectable_item_id);

      if (!category || !winningSelectableId) return;

      const categoryOptions = optionsMap?.[category] || [];
      const winningOption = categoryOptions.find(
        (opt) => Number(opt?.selectable_item_id) === winningSelectableId
      );

      if (!winningOption) return;

      byCategory[category] = winningOption.id;

      if (result?.was_changed) {
        changesByCategory[category] = {
          fromName: result?.name || "",
          toName: result?.winning_name || winningOption?.name || "",
          toId: winningOption.id,
        };
      }
    });

    const all = Array.from(new Set(Object.values(byCategory).filter(Boolean)));
    return {
      selectedMap: { byCategory, all },
      changesByCategory,
    };
  };

  const buildDependencyPopupMessage = async (selectedItem, allItems) => {
    if (!selectedItem) {
      return null;
    }

    const resolveOptionName = async (optionId) => {
      const localMatch = allItems.find((item) => Number(item.id) === Number(optionId));

      if (localMatch?.name) {
        return localMatch.name;
      }

      try {
        const fetchedOption = await apiFetch(`/form/option/${optionId}`, {
          token: user.token,
        });

        return fetchedOption?.name || null;
      } catch {
        return null;
      }
    };

    const blockedOptionIds = [
      ...new Set(
        Object.entries(dependencies)
          .filter(([, blockedIds]) => (blockedIds || []).some((blockedId) => Number(blockedId) === Number(selectedItem.id)))
          .map(([optionId]) => Number(optionId))
      ),
    ];

    const blockedOptionNames = (await Promise.all(
      blockedOptionIds.map((optionId) => resolveOptionName(optionId))
    )).filter(Boolean);

    const blockingOptionNames = (await Promise.all(
      (dependencies[selectedItem.id] || []).map((blockingId) => resolveOptionName(blockingId))
    )).filter(Boolean);

    const messageParts = [];

    if (blockedOptionNames.length === 1) {
      messageParts.push(`Valitsemalla '${selectedItem.name}' et voi valita '${blockedOptionNames[0]}' tuotetta.`);
    } else if (blockedOptionNames.length > 1) {
      messageParts.push(`Valitsemalla '${selectedItem.name}' et voi valita seuraavia tuotteita: ${blockedOptionNames.join(", ")}.`);
    }

    if (blockingOptionNames.length === 1) {
      messageParts.push(`'${selectedItem.name}' ei ole yhteensopiva vaihtoehdon '${blockingOptionNames[0]}' kanssa.`);
    } else if (blockingOptionNames.length > 1) {
      messageParts.push(`'${selectedItem.name}' ei ole yhteensopiva seuraavien vaihtoehtojen kanssa: ${blockingOptionNames.join(", ")}.`);
    }

    if (selectedItem.additional_info) {
      messageParts.push(selectedItem.additional_info);
    }

    return messageParts.length > 0 ? messageParts.join(" ") : null;
  };

  const showInfoPopupForOption = async (optionId, allItems = Object.values(options).flat()) => {
    const selectedItem = allItems.find((item) => Number(item.id) === Number(optionId));
    const dependencyPopupMessage = await buildDependencyPopupMessage(selectedItem, allItems);

    if (!dependencyPopupMessage) {
      setInfoPopup(null);
      return;
    }

    try {
      let message = dependencyPopupMessage;
      const regex = /\(insert option id (\d+) name\)/g;
      let match;

      while ((match = regex.exec(message)) !== null) {
        const referencedId = parseInt(match[1], 10);
        let referencedOpt = allItems.find((item) => Number(item.id) === referencedId);

        if (!referencedOpt) {
          try {
            referencedOpt = await apiFetch(`/form/option/${referencedId}`, {
              token: user.token,
            });
          } catch {
            // Ignore missing option names and keep the original token in place.
          }
        }

        const replacement = referencedOpt?.name || `vaihtoehto ${referencedId}`;
        message = message.replace(match[0], replacement);
      }

      setInfoPopup(message);
    } catch (err) {
      console.error("Error building info popup message:", err);
      setInfoPopup(dependencyPopupMessage);
    }
  };

  const refreshSelections = async () => {
    if (!selectedCompany?.summaryId || !user?.token) return;

    const selData = await apiFetch(`/form/summary/${selectedCompany.summaryId}/selections`, {
      token: user.token,
    });

    let selectedMap = buildSelectedMap(selData);
    let changesByCategory = {};

    if (Object.keys(options || {}).length > 0) {
      const voteResults = await fetchVoteResultsIfFinal(selectedCompany.summaryId, voteStatus?.status);
      if (voteResults.length > 0) {
        const mapped = applyVoteResultOverrides(selectedMap, options, voteResults);
        selectedMap = mapped.selectedMap;
        changesByCategory = mapped.changesByCategory;
      }
    }

    setSelections(selData);
    setSelectedOptions(selectedMap);
    setVoteOutcomeChanges(changesByCategory);
  };

  const handleVoteForOption = async (categoryLabel, option) => {
    if (!selectedCompany?.summaryId || !user?.token || !isBoardMember || !votingOpen) return;

    const categorySelection = selections.find((sel) => sel.category_name === categoryLabel);
    const selectedOptionId = selectedOptions.byCategory?.[categoryLabel];
    const isAgreeVote = Number(selectedOptionId) === Number(option.id);

    if (!categorySelection?.selectable_item_id) {
      alert("Kategorian valintaa ei löytynyt äänestystä varten.");
      return;
    }

    if (!isAgreeVote && !option.selectable_item_id) {
      alert("Vaihtoehdon äänestys ei onnistu ilman selectable_item_id-arvoa.");
      return;
    }

    try {
      setVotingBusy(true);
      await apiFetch(
        `/form/summary/${selectedCompany.summaryId}/selection/${categorySelection.selectable_item_id}/vote`,
        {
          method: "POST",
          token: user.token,
          body: {
            vote_type: isAgreeVote ? "agree" : "alternative",
            alternative_selectable_item_id: isAgreeVote ? null : Number(option.selectable_item_id),
          },
        }
      );

      await refreshSelections();
    } catch (err) {
      alert(err?.body?.error || "Äänen tallentaminen epäonnistui");
    } finally {
      setVotingBusy(false);
    }
  };

  useEffect(() => {
  if (!summaryId || !canEditSelections) return;

  Object.entries(options).forEach(([label, opts]) => {
    if (opts.length === 1 && !selectedOptions.byCategory[label]) {
      const opt = opts[0];

      handleSelect(
        label,
        opt.id,
        opt.type || (opt.isFloorPlan ? "floor_plan" : "option")
      );
    }
  });
}, [options, summaryId, canEditSelections, selectedOptions.byCategory]);

  useEffect(() => {
    if (!selectedCompany?.summaryId || !user?.token || !isBoardMember || !votingOpen || !supportsVoting || votingBusy) {
      return;
    }

    const singleOptionEntries = Object.entries(options).filter(
      ([, opts]) => Array.isArray(opts) && opts.length === 1
    );

    if (singleOptionEntries.length === 0) return;

    async function autoVoteSingleOptionCategories() {
      const votesToCast = singleOptionEntries
        .map(([label, opts]) => {
          const option = opts[0];
          const categorySelection = selections.find((sel) => sel.category_name === label);
          const selectedOptionId = selectedOptions.byCategory?.[label];

          if (!selectedOptionId) return null;

          const isAgreeVote = Number(selectedOptionId) === Number(option.id);

          if (categorySelection?.my_vote_type) return null;
          if (!categorySelection?.selectable_item_id) return null;
          if (!isAgreeVote && !option.selectable_item_id) return null;

          return {
            option,
            categorySelection,
            isAgreeVote,
          };
        })
        .filter(Boolean);

      if (votesToCast.length === 0) return;

      try {
        setVotingBusy(true);

        for (const vote of votesToCast) {
          await apiFetch(
            `/form/summary/${selectedCompany.summaryId}/selection/${vote.categorySelection.selectable_item_id}/vote`,
            {
              method: "POST",
              token: user.token,
              body: {
                vote_type: vote.isAgreeVote ? "agree" : "alternative",
                alternative_selectable_item_id: vote.isAgreeVote
                  ? null
                  : Number(vote.option.selectable_item_id),
              },
            }
          );
        }

        await refreshSelections();
      } catch (err) {
        console.error("Automatic voting for single-option categories failed:", err);
      } finally {
        setVotingBusy(false);
      }
    }

    autoVoteSingleOptionCategories();
  }, [
    options,
    selections,
    selectedOptions.byCategory,
    selectedCompany?.summaryId,
    user?.token,
    isBoardMember,
    votingOpen,
    supportsVoting,
    votingBusy,
  ]);

  useEffect(() => {
    async function loadMissingSelections() {
      if (!checkMissingSelections || !selectedCompany?.summaryId) return;

      const requiredCategories = await fetchRequiredCategories({
        user,
        parentCategory: previousParentCategory,
      });

      const missing = await checkMissingSelections({
        user,
        summaryId: selectedCompany.summaryId,
        requiredCategories,
      });

      setMissingSelections(missing);
    }

    loadMissingSelections();
  }, [selectedCompany, user]);

  // Fetch categories + options + dependencies + selections
  useEffect(() => {
    async function loadData() {
      if (!user || !selectedCompany?.summaryId) return;

      const summaryId = selectedCompany.summaryId;
      setSummaryId(summaryId);
      setUserRole(selectedCompany.role);

      const selData = await apiFetch(`/form/summary/${summaryId}/selections`, {
        token: user.token,
      });
      setSelections(selData);

      const voteStatusData = await apiFetch(`/form/summary/${summaryId}/vote/status`, {
        token: user.token,
      });
      setVoteStatus(voteStatusData);

      try {
        if (customLoader) {
          const data = await customLoader({
            user,
            selectedCompany,
            summaryId,
            url,
            selections: selData
        });

          const loadedOptions = data.options || {};
          let selectedMap = data.selectedOptions || { byCategory: {}, all: [] };
          let changesByCategory = {};

          const voteResults = await fetchVoteResultsIfFinal(summaryId, voteStatusData?.status);
          if (voteResults.length > 0) {
            const mapped = applyVoteResultOverrides(selectedMap, loadedOptions, voteResults);
            selectedMap = mapped.selectedMap;
            changesByCategory = mapped.changesByCategory;
          }

          setOptions(loadedOptions);
          setSelectedOptions(selectedMap);
          setVoteOutcomeChanges(changesByCategory);
          return;
        }

        // parent categories
        const parentCategories = await apiFetch(`/form/category`, {
          token: user.token,
        });

        const parent = parentCategories.find((c) => c.name === parentCategory);
        if (!parent) throw new Error(`Parent category "${parentCategory}" not found`);

        // subcategories
        const subCategories = await apiFetch(`/form/category/${parent.id}/subcategories`, {
          token: user.token,
        });

        // options per subcategory
        let floorPlanId = null;

        if (parentCategory === "Kalusteet") {
          const fpEntry = selData.find((s) => s.floor_plan_id);
          floorPlanId = fpEntry?.floor_plan_id || null;
        }

        const subWithOptions = await Promise.all(
          subCategories.map(async (sub) => {
          const endpoint =
            parentCategory === "Kalusteet" && floorPlanId
              ? `/form/category/${sub.id}/options?floor_plan_id=${floorPlanId}&housing_company_id=${selectedCompany.id}`
              : `/form/category/${sub.id}/options?housing_company_id=${selectedCompany.id}`;

          const optionsData = await apiFetch(endpoint, {
            token: user.token,
          });

        return {
          ...sub,
          options: optionsData.map((opt) => ({
            ...opt,
            image: imageMap[opt.name] || opt.image || null,
          }))
        };
          })
        );

        const renderedOptions = {};
        subWithOptions.forEach((sub) => {
          renderedOptions[sub.name] = sub.options;
        });
        setOptions(renderedOptions);

        // dependencies
        const depMap = {};
        for (const opts of Object.values(renderedOptions)) {
          for (const opt of opts) {
            const depData = await apiFetch(`/form/option/${opt.id}/dependencies`, {
              token: user.token,
            });
            if (depData.length > 0) {
              depMap[opt.id] = depData.map(d => d.blocked_by || d.depends_on_option_id);
            }
          }
        }
        setDependencies(depMap);

        // current selections
        let selectedMap = buildSelectedMap(selData);
        let changesByCategory = {};
        const voteResults = await fetchVoteResultsIfFinal(summaryId, voteStatusData?.status);

        if (voteResults.length > 0) {
          const mapped = applyVoteResultOverrides(selectedMap, renderedOptions, voteResults);
          selectedMap = mapped.selectedMap;
          changesByCategory = mapped.changesByCategory;
        }

        setSelectedOptions(selectedMap);
        setVoteOutcomeChanges(changesByCategory);

      } catch (err) {
        console.error(`Error loading data for ${currentStep}:`, err);
      }
    }

    loadData();
  }, [user, selectedCompany]);

  // Select option
  async function handleSelect(label, id, type = "option") {

  if (!canEditSelections) return;

  setSelectedOptions(prev => {
    const newByCategory = { ...prev.byCategory, [label]: id };
    const allSet = new Set(prev.all);
    if (prev.byCategory[label]) allSet.delete(prev.byCategory[label]);
    allSet.add(id);
    return { byCategory: newByCategory, all: Array.from(allSet) };
  });

  const allItems = Object.values(options).flat();
  await showInfoPopupForOption(id, allItems);

  if (!summaryId) return;

  try {
    await apiFetch(`/form/summary/${summaryId}/select-all`, {
      method: "POST",
      token: user.token,
      body: {
        type,
        item_id: id,
        is_selected: true,
        current_step: currentStep,
      },
    });

    window.dispatchEvent(new Event("cart-updated"));
  } catch (err) {
    if (err?.status === 403) {
      alert("Valintoja ei voi muokata, koska äänestys on käynnissä tai valinnat on lukittu.");
      return;
    }
    console.error("Unified selection failed:", err);
  }}

  const allSelected = Object.keys(options).every(
    label => selectedOptions.byCategory[label]
  );

  async function handleNext() {
    if (!summaryId) return alert("Odotetaan yhteenvetoa...");
    if (!canBrowseReadOnlyPages && !allSelected) {
      return alert("Valitse vaihtoehto jokaisesta kategoriasta!");
    }

    setSaving(true);

    try {
      navigate(nextRoute);
    } catch (err) {
      console.error("Saving failed:", err);
    } finally {
      setSaving(false);
    }
  }

  const isDisabled = saving || !summaryId || (!canBrowseReadOnlyPages && (!allSelected || !canEditSelections));

  if (checkMissingSelections && missingSelections.length > 0) {
  return (
    <>
      <Header />
      <div className="options-form-container">
        <Sidebar />

        <main className="options-form-content">
          <div className="warning-container">
            <img src={infored} className="info-red-icon" alt="" />
            <p className="warning-title">Puuttuvat valinnat</p>
          </div>

          {Object.entries(
            missingSelections.reduce((acc, m) => {
              if (!acc[m.page]) acc[m.page] = [];
              acc[m.page].push(m.label);
              return acc;
            }, {})
          ).map(([page, labels], i) => (
            <p key={i} className="warning-text">
              Sinulta puuttuu <strong>{labels.join(", ")}</strong> valinta{" "}
              <strong>{page}</strong> -sivulta. Tee puuttuvat valinnat ensin.
            </p>
          ))}
        </main>
      </div>
      <Footer />
    </>
  )}

  return (
  <>
  <Header />
  <div className="options-form-container">
    <Sidebar />

    <main className="options-form-content">
      {!hideTitle && <h1 className="default-title">{title}</h1>}

      {customRenderer ? (
  (() => {
    const Renderer = customRenderer;
    return (
      <Renderer
        options={options}
        selectedOptions={selectedOptions}
        onSelect={handleSelect}
        missingSelections={missingSelections}
        userRole={userRole}
        voteStatus={voteStatus}
        selections={selections}
        onVoteOption={supportsVoting ? handleVoteForOption : null}
        votingBusy={votingBusy}
        voteOutcomeChanges={voteOutcomeChanges}
      />
    );
  })()
    ) : (
        <>
          {Object.entries(options).map(([label, opts]) => (
            <div key={label} className="category-section card-box">
              {pageType !== "furniture-arrangement" && label && (
                <div className="category-title-wrapper">
                  <p className="category-title">{label}</p>

                <div className="selection-display">
                  <span className="default-text"><strong>Valittu:</strong></span>
                  <span className="selected-badge">
                    {selectedOptions.byCategory[label]
                      ? opts.find(opt => opt.id === selectedOptions.byCategory[label])?.name
                      : "ei valintoja"}
                  </span>
                </div>
                </div>
              )}

              {opts.map(opt => {
                const categorySelection = selections.find((sel) => sel.category_name === label);
                const selectedOptionId = selectedOptions.byCategory?.[label];
                const isSelectedOption = Number(selectedOptionId) === Number(opt.id);
                const isBlockedByDependency = (dependencies[opt.id] || []).some((blockedId) =>
                  selectedOptions.all
                    .map(Number)
                    .includes(Number(blockedId))
                );
                const myAlternativeSelectableId = Number(categorySelection?.my_alternative_selectable_item_id);
                const changedVoteInfo = voteOutcomeChanges?.[label];
                const isMyVote = (categorySelection?.my_vote_type === "agree" && isSelectedOption)
                  || (categorySelection?.my_vote_type === "alternative" && myAlternativeSelectableId === Number(opt.selectable_item_id));
                const canVoteOption = Boolean(categorySelection?.selectable_item_id)
                  && (isSelectedOption || Boolean(opt.selectable_item_id));

                return (
                <div
                  key={opt.id}
                  className="option-container"
                  onClick={() => {
                    if (isBlockedByDependency) {
                      void showInfoPopupForOption(opt.id);
                    }
                  }}
                >
                  <div className="option-header">
                    <div className="option-header-left">
                      <input
                        type="radio"
                        name={`option-${label}`}
                        checked={selectedOptions.byCategory[label] === opt.id}
                        disabled={
                          isBlockedByDependency ||
                          !canEditSelections
                        }
                        onChange={() =>
                          handleSelect(
                            label,
                            opt.id,
                            opt.type || (opt.isFloorPlan ? "floor_plan" : "option") //opt.isFloorPlan ? "floor_plan" : "option"
                          )
                        }
                      />
                      <h3>{opt.name}</h3>
                      {changedVoteInfo && Number(changedVoteInfo.toId) === Number(opt.id) && (
                        <span
                          className="vote-change-badge"
                          title={`Valinta muuttui äänestyksessä: ${changedVoteInfo.fromName} -> ${changedVoteInfo.toName}`}
                        >
                          <i className="fa-solid fa-circle-info" aria-hidden="true" />
                          Äänestyksessä muutettu
                        </span>
                      )}
                    </div>

                    {isBoardMember && votingOpen && supportsVoting && (
                      <button
                        type="button"
                        className={`vote-button ${isMyVote ? "selected" : ""}`}
                        disabled={votingBusy || !canVoteOption}
                        onClick={() => handleVoteForOption(label, opt)}
                      >
                        {isMyVote ? "Äänestetty" : "Äänestä"}
                      </button>
                    )}
                  </div>

                  <p className="default-text">{opt.description}Suscipit gravida risus sit amet pellentesque. Nulla luctus sagittis felis, eget mollis elit
                    sollicitudin in. Donec at fermentum urna. Morbi a accumsan diam, a cursus mi.</p>

                  {opt.image ? (
                    <img src={getImageUrl(opt.image)} alt={opt.name} />
                  ) : pageType === "furniture-arrangement" ? (
                    <img
                      src={getImageUrl(opt.image)}
                      alt="Ei kuvaa"
                      className="option-placeholder"
                    />
                  ) : null}
                </div>
              )})}
            </div>
          ))}

          <div className="next-button-container">
            <button
              className="next-button"
              disabled={isDisabled}
              onClick={handleNext}
            >
              {saving ? "Tallennetaan..." : "Siirry Seuraavaan"}
            </button>
          </div>
        </>
      )}
    </main>
  </div>

  <Footer />

  {/* Info popup */}
  {infoPopup && (
    <div className="popup-overlay" onClick={() => setInfoPopup(null)}>
      <div className="popup-box" onClick={(e) => e.stopPropagation()}>
        <div className="warning-container">
          <img src={infoyellow} alt="" className="info-red-icon" />
          <p className="warning-title">Valinnan ohjeistus</p>
        </div>

        <p className="default-text">{infoPopup}</p>

        <button
          onClick={() => setInfoPopup(null)}
          className="popup-close-icon"
        >
          <i className="fa-regular fa-circle-xmark" />
        </button>
      </div>
    </div>
  )}
</>
);
}